import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout
} from '@stripe/react-stripe-js';
import { useTranslation } from 'react-i18next';
import { getStripeLanguage } from '../../i18n';
import { StripeService } from '../../services';
import styles from './styles.module.css';

const STRIPE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

const StripeCheckoutModal = ({
  isOpen,
  onClose,
  tier,
  userId,
  email,
  returnUrl,
  isGuest = false,
  checkoutType = 'subscription',
  packageCode,
  promotionCode,
  offerCode,
  campaignArtGenerationId,
  language,
  periodicity = 'month',
}) => {
  const { i18n, t } = useTranslation();
  const [checkoutError, setCheckoutError] = useState(null);
  const fetchRequestIdRef = useRef(0);

  const locale = useMemo(() => {
    const currentLng = (
      language
      || localStorage.getItem('i18nextLng')
      || i18n.language
      || ''
    ).toLowerCase();

    if (currentLng.includes('pt')) {
      return 'pt-BR';
    }

    if (currentLng.includes('es')) {
      return 'es';
    }

    if (currentLng.includes('en')) {
      return 'en';
    }

    return 'auto';
  }, [i18n.language, language]);

  const stripePromise = useMemo(() => {
    return loadStripe(STRIPE_KEY, { locale });
  }, [locale]);

  const checkoutParamsRef = useRef({
    tier,
    userId,
    email,
    returnUrl,
    isGuest,
    checkoutType,
    packageCode,
    promotionCode,
    offerCode,
    campaignArtGenerationId,
    language,
    periodicity,
  });

  checkoutParamsRef.current = {
    tier,
    userId,
    email,
    returnUrl,
    isGuest,
    checkoutType,
    packageCode,
    promotionCode,
    offerCode,
    campaignArtGenerationId,
    language,
    periodicity,
  };

  const fetchClientSecret = useCallback(async () => {
    const requestId = fetchRequestIdRef.current + 1;
    fetchRequestIdRef.current = requestId;

    const params = checkoutParamsRef.current;

    try {
      let result;

      if (params.checkoutType === 'addon') {
        result = await StripeService.createAddonCheckoutSession({
          packageCode: params.packageCode,
          email: params.email || undefined,
          returnUrl: params.returnUrl || undefined,
          promotionCode: params.promotionCode || undefined,
          offerCode: params.offerCode || undefined,
          campaignArtGenerationId: params.campaignArtGenerationId || undefined,
          language: params.language || getStripeLanguage(),
        });
      } else if (params.isGuest) {
        result = await StripeService.createGuestCheckoutSession({
          tier: Number(params.tier),
          email: params.email || undefined,
          returnUrl: params.returnUrl || undefined,
          language: params.language || getStripeLanguage(),
          periodicity: params.periodicity || 'month',
        });
      } else {
        result = await StripeService.createCheckoutSession({
          tier: Number(params.tier),
          userId: params.userId,
          email: params.email || undefined,
          returnUrl: params.returnUrl || undefined,
          offerCode: params.offerCode || undefined,
          language: params.language || getStripeLanguage(),
          periodicity: params.periodicity || 'month',
        });
      }

      if (fetchRequestIdRef.current !== requestId) {
        throw new Error('Checkout cancelado');
      }

      if (result.responseStatus === 200 && result.data?.clientSecret) {
        setCheckoutError(null);
        return result.data.clientSecret;
      }

      const message =
        result.message
        || result.data?.error?.message
        || result.data?.message
        || t('stripe.checkout_error');

      setCheckoutError(message);
      throw new Error(message);
    } catch (error) {
      if (fetchRequestIdRef.current === requestId) {
        const message = error?.message || t('stripe.checkout_error');
        setCheckoutError(message);
      }

      console.error('Error fetching client secret:', error);
      throw error;
    }
  }, []);

  const checkoutKey = `${checkoutType}-${tier ?? 'none'}-${packageCode ?? 'none'}-${campaignArtGenerationId ?? 'none'}-${promotionCode ?? 'none'}-${offerCode ?? 'none'}-${language || getStripeLanguage()}-${periodicity}`;

  useEffect(() => {
    if (isOpen) {
      setCheckoutError(null);
      fetchRequestIdRef.current += 1;
    }
  }, [isOpen, checkoutKey]);

  const options = useMemo(
    () => ({ fetchClientSecret }),
    [fetchClientSecret],
  );

  if (!isOpen) return null;

  const modal = (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
        <button type="button" className={styles.closeButton} onClick={onClose}>&times;</button>
        <div id="checkout" className={styles.checkoutWrapper}>
          {!STRIPE_KEY ? (
            <p className={styles.checkoutError}>
              {t('stripe.missing_publishable_key')}
            </p>
          ) : checkoutError ? (
            <p className={styles.checkoutError}>{checkoutError}</p>
          ) : (
            <EmbeddedCheckoutProvider
              key={checkoutKey}
              stripe={stripePromise}
              options={options}
            >
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          )}
        </div>
      </div>
    </div>
  );

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(modal, document.body);
  }

  return modal;
};

export default StripeCheckoutModal;
